import { Icon, IconButton, Stack, Typography } from '@mui/material';
import { AttendanceCode } from '@schooly/api';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import {
  ATTENDANCE_CODE_ACTION_OPTIONS,
  ATTENDANCE_CODE_IN_SCHOOl_OPTIONS,
  ATTENDANCE_CODE_IS_PRESENT_OPTIONS,
  AttendanceCodeAction,
  AttendanceCodeIsPresent,
} from '@schooly/constants';
import { Attention2Icon, CrossIcon, NewTabIcon, SmallButton, WarningBlock } from '@schooly/style';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { FormRadioGroup } from '../../../../components/uikit-components/FormCheckbox/FormRadioGroup';
import { ControlTextField } from '../../../../components/uikit-components/FormTextField/ControlTextField';
import { ControlModalHeaderInput } from '../../../../components/uikit-components/Modal/ControlModalHeaderInput';
import {
  ModalContent,
  ModalMain,
  ModalSmall,
} from '../../../../components/uikit-components/Modal/Modal.styled';
import { ModalFooterWithActions } from '../../../../components/uikit-components/Modal/ModalFooterWithActions';
import { ModalHeader } from '../../../../components/uikit-components/Modal/ModalHeader';
import { AttendanceCodeForm } from '../../../../hooks/useAttendanceCodes';
import { useNotificationSettings } from '../../../../hooks/useNotificationSettings';
import { AttendanceCodeCreate, getDefaultCodeFormValues } from './utils';

export type AddAttendanceCodeModalContentProps = {
  code: AttendanceCodeCreate | AttendanceCode;
  isEditing: boolean;
  isLoading: boolean;
  isDeleting: boolean;
  onDelete?: () => void;
  onClose: () => void;
  onSubmit: (v: AttendanceCodeForm) => void;
  validateCode: (code: AttendanceCodeForm['code'], id?: AttendanceCodeForm['id']) => string | true;
  canEditNotification?: boolean;
};

export const AddAttendanceCodeModalContent: FC<AddAttendanceCodeModalContentProps> = ({
  code,
  isDeleting,
  isEditing,
  isLoading,
  onDelete,
  onSubmit,
  onClose,
  validateCode,
  canEditNotification,
}) => {
  const { formatMessage } = useIntl();
  const { attendanceNotificationsEnabled } = useNotificationSettings();
  const { getConfirmation } = useConfirmationDialog();

  const form = useForm<AttendanceCodeForm>({
    defaultValues: getDefaultCodeFormValues(code),
    mode: 'onChange',
  });

  const codeValue = form.watch('code');
  const nameValue = form.watch('name');
  const isPresentValue = form.watch('is_present');
  const actionValue = form.watch('action');

  const actionOptions = useMemo(() => {
    if (isPresentValue) {
      return ATTENDANCE_CODE_ACTION_OPTIONS.filter(
        (option) => option.value !== AttendanceCodeAction.RequestJustification,
      );
    }

    return ATTENDANCE_CODE_ACTION_OPTIONS;
  }, [isPresentValue]);

  useEffect(() => {
    const subscription = form.watch(({ is_present, action }, { name }) => {
      if (
        name === 'is_present' &&
        is_present === AttendanceCodeIsPresent.Present &&
        action === AttendanceCodeAction.RequestJustification
      ) {
        form.setValue('action', AttendanceCodeAction.NoAction);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const showWarning =
    !attendanceNotificationsEnabled && actionValue === AttendanceCodeAction.NotifyParents;

  const onCloseModal = useCallback(async () => {
    if (form.formState.isDirty) {
      const isConfirmed = await getConfirmation({
        textId: 'school-edit-CloseUnsavedConfirmation',
      });
      if (!isConfirmed) return;
    }
    onClose();
  }, [form.formState.isDirty, getConfirmation, onClose]);

  return (
    <ModalSmall open onClose={onCloseModal}>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <ModalHeader
            active
            title={
              <ControlModalHeaderInput
                control={form.control}
                name="name"
                rules={{
                  required: true,
                  onChange: (e) => {
                    if (!codeValue && !nameValue && `${e.target.value}`) {
                      form.setValue('code', `${e.target.value}`.charAt(0));
                      form.trigger('code');
                    }
                  },
                }}
                placeholder={formatMessage({ id: 'attendance-CodeName' })}
                autoFocus
              />
            }
          >
            {(!isLoading || !isDeleting) && (
              <IconButton onClick={onCloseModal}>
                <CrossIcon />
              </IconButton>
            )}
          </ModalHeader>
          <ModalMain>
            <ModalContent active>
              <Stack gap={3} sx={{ height: '100%' }}>
                <Stack gap={1}>
                  <Typography variant="h2">
                    {formatMessage({ id: 'attendance-CountAsPresent' })}
                  </Typography>
                  <Typography variant="h3" color="text.primary" mb={1}>
                    {formatMessage({ id: 'attendance-CountAsPresent-Description' })}
                  </Typography>
                  <FormRadioGroup options={ATTENDANCE_CODE_IS_PRESENT_OPTIONS} name="is_present" />
                </Stack>

                <Stack gap={1}>
                  <Typography variant="h2">
                    {formatMessage({ id: 'attendance-CountAsOnPremises' })}
                  </Typography>
                  <Typography variant="h3" color="text.primary" mb={1}>
                    {formatMessage({ id: 'attendance-CountAsOnPremises-Description' })}
                  </Typography>
                  <FormRadioGroup options={ATTENDANCE_CODE_IN_SCHOOl_OPTIONS} name="in_school" />
                </Stack>

                {canEditNotification && (
                  <Stack gap={2}>
                    <Typography variant="h2">
                      {formatMessage({ id: 'attendance-ShouldTriggerAction' })}
                    </Typography>
                    {showWarning && (
                      <WarningBlock>
                        <Stack direction="row" gap={2} alignItems="center">
                          <Icon
                            sx={(theme) => ({
                              color: theme.palette.background.paper,
                              path: { color: theme.palette.common.orange },
                            })}
                          >
                            <Attention2Icon />
                          </Icon>
                          <Typography variant="h3" color="inherit">
                            {formatMessage({ id: 'attendance-NotificationDisabledWarning' })}
                          </Typography>

                          <Link to="/settings/notifications" target="_blank">
                            <SmallButton
                              sx={{ textWrap: 'nowrap', flexShrink: 0 }}
                              endIcon={
                                <Icon sx={{ p: 0.25 }}>
                                  <NewTabIcon />
                                </Icon>
                              }
                            >
                              {formatMessage({ id: 'school-Settings-GoToSettings' })}
                            </SmallButton>
                          </Link>
                        </Stack>
                      </WarningBlock>
                    )}

                    <FormRadioGroup options={actionOptions} name="action" />
                  </Stack>
                )}

                <Stack gap={2}>
                  <Typography variant="h2">
                    {formatMessage({ id: 'attendance-AddCode' })}
                  </Typography>
                  <ControlTextField
                    name="code"
                    control={form.control}
                    label={formatMessage({ id: 'attendance-Code' })}
                    fullWidth
                    canClear
                    rules={{
                      required: true,
                      validate: (v) => validateCode(`${v}`, 'id' in code ? code.id : undefined),
                    }}
                    hideLabel
                    sx={{ width: 100, '& .Mui-error': { textWrap: 'nowrap' } }}
                  />

                  <ControlTextField
                    name="description"
                    control={form.control}
                    label={formatMessage({ id: 'attendance-CodeDescription' })}
                    fullWidth
                    multiline
                    rows={2}
                    canClear
                  />
                </Stack>
              </Stack>
            </ModalContent>
          </ModalMain>
          <ModalFooterWithActions
            isNewItem={!isEditing}
            saving={isLoading}
            showDeleteButton={isEditing}
            deleting={isDeleting}
            disabled={isDeleting || isLoading}
            onDeleteClick={onDelete}
            active
          />
        </form>
      </FormProvider>
    </ModalSmall>
  );
};
